import { z } from 'zod';
import { canadianTimeZoneSchema, DEFAULT_SITE_TIME_ZONE } from './timezone';
import { provincesEnum } from './payor';

const postalCodeRegex = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/;
const faxRegex = /^\d{10}$/;

export const HEALTH_REGION_OPTIONS = [
  {
    group: 'Manitoba',
    items: [
      { value: 'Winnipeg', label: 'Winnipeg' },
      { value: 'Southern', label: 'Southern' },
      { value: 'Northern - MB', label: 'Northern' },
      { value: 'Prairie Mountain', label: 'Prairie Mountain' },
      { value: 'Interlake', label: 'Interlake' },
    ],
  },
  {
    group: 'Saskatchewan',
    items: [
      { value: 'R', label: 'Regina' },
      { value: 'S', label: 'Saskatoon' },
      { value: 'X', label: 'X - Rural (Northern)' },
    ],
  },
  {
    group: 'British Columbia',
    items: [
      { value: 'Vancouver Coastal', label: 'Vancouver Coastal' },
      { value: 'Fraser', label: 'Fraser' },
      { value: 'Interior', label: 'Interior' },
      { value: 'Island', label: 'Island' },
      { value: 'Northern - BC', label: 'Northern' },
    ],
  },
];

export const siteContactValidationMessages = {
  postalCode: 'Postal code must be in A1A 1A1 format.',
  fax: 'Fax must be 10 digits with no dashes.',
} as const;

export const normalizePostalCodeInput = (value: string) => {
  const cleaned = value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 6);
  if (cleaned.length <= 3) return cleaned;
  return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
};

export const normalizeFaxInput = (value: string) =>
  value.replace(/\D/g, '').slice(0, 10);

export const isValidPostalCode = (value: string) => postalCodeRegex.test(value);

export const isValidFax = (value: string) => faxRegex.test(value);

export const validateSiteContactValues = ({
  postalCode,
  fax,
}: {
  postalCode: string | null | undefined;
  fax: string;
}) => {
  const normalizedPostalCode =
    typeof postalCode === 'string' && postalCode.trim().length > 0
      ? normalizePostalCodeInput(postalCode)
      : null;
  const normalizedFax = normalizeFaxInput(fax);
  const postalCodeError =
    normalizedPostalCode !== null && !isValidPostalCode(normalizedPostalCode)
      ? siteContactValidationMessages.postalCode
      : null;
  const faxError = isValidFax(normalizedFax)
    ? null
    : siteContactValidationMessages.fax;

  return {
    normalizedPostalCode,
    normalizedFax,
    errors: {
      postalCode: postalCodeError,
      fax: faxError,
    },
    isValid: !postalCodeError && !faxError,
  };
};

export const siteContactFormSchema = z.object({
  postalCode: z
    .union([z.string(), z.null()])
    .transform((value) => (typeof value === 'string' ? value.trim() : value))
    .refine(
      (value) => value === null || value === '' || postalCodeRegex.test(value),
      {
        message: 'Postal code must be in A1A 1A1 format.',
      }
    ),
  fax: z.string().refine((value) => faxRegex.test(value), {
    message: 'Fax must be 10 digits with no dashes.',
  }),
});

export const siteSettingsSchema = z.object({
  defaultPayorID: z.guid().nullable().default(null),
});

export type SiteSettings = z.infer<typeof siteSettingsSchema>;

export const siteIDsSchema = z.preprocess((value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    return value.split(',').map((siteID) => siteID.trim());
  }

  return value;
}, z.array(z.guid()).min(1));

export type SiteIDs = z.infer<typeof siteIDsSchema>;

export const siteSchema = z.object({
  id: z.guid(),
  name: z.string(),
  address: z.string(),
  fax: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  postalCode: z.string().nullable(),
  region: z.string(),
  status: z.string(),
  city: z.string(),
  province: provincesEnum,
  description: z.string(),
  proFees: z.boolean(),
  techFees: z.boolean(),
  organizationID: z.string().nullable(),
  siteNumber: z.string(),
  hospitalCode: z.string().nullable(),
  overhead: z.number(),
  invoicee: z.enum(['site', 'provider']).default('site').nullable(),
  invoiceRate: z.number().nullable(),
  apiID: z.string().nullable(),
  apiSecret: z.string().nullable(),
  apiKey: z.string().nullable(),
  autoRemit: z.boolean().default(false).nullable(),
  etransfer: z.boolean().default(false),
  creditCard: z.boolean().default(false),
  cheque: z.boolean().default(false),
  etransferEmail: z.string().nullable().default(null),
  etransferPasswordRequirement: z.string().nullable().default(null),
  creditCardPhoneNumber: z.string().nullable().default(null),
  requiresTariffIcd9: z.boolean().default(true),
  timezone: canadianTimeZoneSchema.default(DEFAULT_SITE_TIME_ZONE),
  allowUploads: z.boolean().default(true),
  settings: siteSettingsSchema.default({ defaultPayorID: null }),
});

export const siteSummarySchema = siteSchema
  .pick({
    id: true,
    name: true,
    siteNumber: true,
  })
  .strict();
